import {
  Typography,
  Grid,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  useTheme,
  Alert,
  useMediaQuery,
  TextField,
} from "@mui/material";
import MyToastShow from "@/components/Layout/MyToast";
import { useState } from "react";
import { useEliminarUserMutation } from "@/redux/api/userApi";
import { useForgotPasswordMutation } from "../../redux/api/authApi";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import LockIcon from '@mui/icons-material/Lock';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EmailIcon from '@mui/icons-material/Email';
import { LoadingButton } from "@mui/lab";

const Seguridad = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useAppSelector((state) => state.userState.user);
  const dispatch = useAppDispatch();
  
  // Password reset states
  const [forgotPassword, { isLoading: isSendingResetEmail }] = useForgotPasswordMutation();

  // Account deletion states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const [eliminarUser] = useEliminarUserMutation();

  // Handle password reset email
  const handleSendResetEmail = async () => {
    if (!user?.email) {
      MyToastShow({ message: 'No se encontró el email del usuario', type: 'error' });
      return;
    }

    try {
      const res = await forgotPassword(user.email).unwrap();
      MyToastShow({ 
        message: 'Se ha enviado un correo electrónico con las instrucciones para restablecer tu contraseña', 
        type: 'success' 
      });
    } catch (error: any) {
      MyToastShow({ 
        message: error?.data?.message || 'Error al enviar el correo de restablecimiento', 
        type: 'error' 
      });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'ELIMINAR') {
      MyToastShow({ message: 'Debe escribir "ELIMINAR" para confirmar', type: 'error' });
      return;
    }

    setIsDeleting(true);
    try {
      await eliminarUser({ id: Number(user?.id || 0), code: 'ELIMINAR' }).unwrap();
      MyToastShow({ message: 'Cuenta eliminada correctamente', type: 'success' });
      dispatch({ type: 'userSlice/logout' });
    } catch (error) {
      MyToastShow({ message: 'Error al eliminar la cuenta', type: 'error' });
    } finally {
      setIsDeleting(false);
      setOpenDeleteDialog(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
          Configuración de Seguridad
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Gestiona la seguridad de tu cuenta y contraseña
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Change Password Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={3}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main + '15',
                    color: theme.palette.primary.main
                  }}
                >
                  <LockIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Cambiar Contraseña
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Actualiza tu contraseña para mantener tu cuenta segura
                  </Typography>
                </Box>
              </Box>

              <Box>
                <Alert 
                  severity="info" 
                  sx={{ mb: 3 }}
                  icon={<EmailIcon />}
                >
                  <Typography variant="body2">
                    Por tu seguridad, te enviaremos un correo electrónico con las instrucciones para restablecer tu contraseña.
                  </Typography>
                </Alert>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Correo electrónico: <strong>{user?.email}</strong>
                </Typography>
                
                <LoadingButton
                  loading={isSendingResetEmail}
                  onClick={handleSendResetEmail}
                  variant="contained"
                  startIcon={<EmailIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Enviar Correo de Restablecimiento
                </LoadingButton>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Danger Zone */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid', borderColor: 'error.light' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.error.main + '15',
                    color: theme.palette.error.main
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </Box>
                <Typography variant="h6" color="error">
                  Zona de Peligro
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" mb={2}>
                Esta acción eliminará permanentemente tu cuenta y todos los datos asociados.
              </Typography>
              
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setOpenDeleteDialog(true)}
                sx={{ 
                  borderColor: theme.palette.error.light,
                  color: theme.palette.error.main,
                  '&:hover': {
                    borderColor: theme.palette.error.main,
                    backgroundColor: theme.palette.error.main + '08'
                  }
                }}
              >
                Eliminar Cuenta
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Account Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">
          Eliminar Cuenta Permanentemente
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Esta acción es irreversible. Se eliminarán todos tus datos, carpetas e informes.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Para confirmar la eliminación, escribe <strong>ELIMINAR</strong> en el campo de abajo:
          </Typography>
          <TextField
            fullWidth
            placeholder="Escriba ELIMINAR para confirmar"
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>
            Cancelar
          </Button>
          <LoadingButton
            loading={isDeleting}
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={deleteConfirmation !== 'ELIMINAR'}
          >
            Eliminar Cuenta
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Seguridad;


